import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { GiftsService, GiftRedemptionPreview, GiftRedemptionPreviewAnomaly } from "@services/dws/gifts.service";
import { toast } from '@web/util/toast';
import { tr } from "@util/tr";
import { Gift } from '@interfaces/dws/gift';
import { ReservationsService } from "@services/reservations.service";
import { locale } from "@util/locale";

@Component({
  selector: 'app-gift-redeem',
  templateUrl: './gift-redeem.component.html',
  styleUrls: ['./gift-redeem.component.scss']
})
export class GiftRedeemComponent implements OnInit {
  giftId!: string;
  reservationId: string | null = null;
  showRedeemInput: boolean = false;
  isLoadingRedeem: boolean = false;
  preview?: GiftRedemptionPreview;
  previewLoading = true;
  previewUnavailable = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { gift: Gift },
    private dlg: MatDialogRef<GiftRedeemComponent>,
    private giftsService: GiftsService,
    private reservationService: ReservationsService
  ) {
    this.giftId = this.data.gift.giftId;
  }

  ngOnInit(): void {
    this.giftsService.redemptionPreview(this.giftId).subscribe({
      next: (p) => {
        this.preview = p ?? {};
        this.previewLoading = false;
      },
      error: () => {
        this.previewLoading = false;
        this.previewUnavailable = true;
      }
    });
  }

  anomalyText(a: GiftRedemptionPreviewAnomaly): string {
    const useIt = locale().locale === 'it';
    const msg = useIt ? a.messageIt : a.messageEn;
    return (msg || a.code || '').trim();
  }

  closeClicked(): void {
    this.dlg.close();
  }

  onRedeemClick(): void {
    this.showRedeemInput = true;
  }

  onLinkClick(): void {
    this.dlg.close({ action: 'link' });
  }

  onConfirmRedeem(): void {
    this.isLoadingRedeem = true;
    // Check if a reservation id was provided and if it is valid
    if (this.reservationId && this.reservationId.trim() !== '') {
      this.reservationService.getCrm(this.reservationId).subscribe({
        next: () => {
          // Reservation id is valid, proceed with redemption
          this.redeemGift();
        },
        error: err => {
          this.isLoadingRedeem = false;
          if (err.status === 400) {
            toast(tr("The reservation id was invalid."));
          } else if (err.status === 404) {
            toast(tr("The reservation was not found."));
          } else {
            toast(tr("The reservation could not be found."));
          }
        }
      });
    } else {
      this.redeemGift();
    }
  }

  private redeemGift(): void {
    this.giftsService.redeem(this.giftId).subscribe({
      next: () => {
        if (this.reservationId && this.reservationId.trim() !== '') {
          // Reservation id was provided and is valid, proceed with association
          this.associateReservation(this.reservationId);
        } else {
          this.isLoadingRedeem = false;
          toast(tr("The gift has been redeemed."));
          this.dlg.close({ action: 'redeemed', reservationId: null });
        }
      },
      error: err => {
        this.isLoadingRedeem = false;
        console.error("Error redeeming gift", err);
        if (err.status === 404) {
          toast(tr("The gift was not found. It may have been removed or is no longer available."));
        } else if (err.status === 400) {
          toast(tr("Unable to complete the forced redemption. Verify the gift or try again later."));
        } else {
          toast(tr("An error occurred while redeeming the gift. Please try again later."));
        }
      }
    });
  }

  associateReservation(reservationId: string): void {
    this.reservationService.attachGift(reservationId, this.giftId).subscribe({
      next: () => {
        this.isLoadingRedeem = false;
        toast(tr("The gift has been redeemed and attached to the reservation."));
        this.dlg.close({ action: 'redeemed', reservationId: reservationId });
      },
      error: err => {
        this.isLoadingRedeem = false;
        console.error("Error attaching gift to reservation", err);
        if (err.status === 400) {
          toast(tr("The gift was redeemed but the reservation id was invalid."));
        } else if (err.status === 404) {
          toast(tr("The gift was redeemed but the reservation was not found."));
        } else {
          toast(tr("The gift was redeemed but could not be attached to the reservation."));
        }
        this.dlg.close({ action: 'redeemed', reservationId: null });
      }
    });
  }

  onCancelRedeem(): void {
    this.showRedeemInput = false;
    this.reservationId = null;
  }
}
